<template>
    <div
        :id="id"
        :class="{ dense: dense }"
        class="tl-input-textarea"
        data-form-validator
    >
        <div
            :class="{ 'animation-shake': error }"
            class="input-wrapper"
        >
            <div
                class="label-input-wrapper textarea"
                :class="{ error: error, focus: isFocused, smallLabel: (isFocused || modelState) && label, disabled: disabled, opaq: opaq }"
                @click="select()"
            >
                <div class="textarea-wrapper">
                    <tl-position
                        class="label h-full"
                        y="start"
                    >
                        <div>{{ label }}</div>
                    </tl-position>

                    <div
                        v-if="secret && !showSecret"
                        class="mask"
                        style=""
                    >
                        {{ mask }}
                    </div>

                    <textarea
                        ref="input"
                        v-model="modelState"
                        :class="{ secret, 'transparent': secret && !showSecret }"
                        :disabled="disabled"
                        tabindex="0"
                        @blur="isFocused = false"
                        @focus="isFocused = true"
                        @input="keyup"
                    ></textarea>

                    <Button
                        v-if="secret"
                        severity="contrast"
                        :icon="`pi ${showSecret ? 'pi-eye-slash' : 'pi-eye'}`"
                        outlined
                        size="small"
                        class="secret-button"
                        @click="showSecret = !showSecret"
                    />
                </div>
            </div>
        </div>
        <tl-input-error-text
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, inject } from 'vue'
import uniqid from 'uniqid'
import useRules from './validator/rules'
import { useEventBus } from '@vueuse/core'
import { getInjects } from '@/components/$tl/utils/injects'

const props = defineProps<{
    modelValue?: string
    label?: string
    opaq?: boolean
    focus?: boolean
    disabled?: boolean
    rules?: ANY[]
    dense?: boolean
    external?: boolean
    keyup?: () => void
    select?: boolean
    secret?: boolean
    error?: string | boolean
}>()

const emit = defineEmits(['validated', 'resetError', 'focused', 'update:modelValue'])

const injects = getInjects(props, inject)
const { validateLogic } = useRules()

const id = ref(uniqid())
const input = ref<HTMLInputElement>()
const modelState = ref('')
const isFocused = ref(false)
const internalError = ref(null)
const showSecret = ref(!props.secret)

const modelValue = computed(() => props.modelValue ?? injects.modelValue?.value)
const label = computed(() => props.label ?? injects.label?.value)
const opaq = computed(() => props.opaq ?? injects.opaq?.value)
// const focus = computed(() => props.focus ?? injects.focus?.value)
const disabled = computed(() => props.disabled ?? injects.disabled?.value)
// const rules = computed(() => props.rules ?? injects.rules?.value)
const dense = computed(() => props.dense ?? injects.dense?.value)
// const external = computed(() => props.external ?? injects.external?.value)
const error = computed(() => (props.error ? props.error : internalError.value))
const secret = computed(() => props.secret ?? injects.secret?.value)

modelState.value = modelValue.value

const mask = computed(() => modelState.value.replace(/\S/g, '•'))

const focusFn = () => {
    input.value?.focus()
    isFocused.value = true
}

const select = () => {
    if (props.disabled) return
    focusFn()
    isFocused.value = true
}

const resetError = () => {
    internalError.value = null
    emit('resetError')
}

watch(isFocused, (val) => emit('focused', val))
watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
})
watch(modelState, (val) => {
    emit('update:modelValue', val)
    if (error.value && val.length) {
        resetError()
    }
})

onMounted(() => {
    if (props.select) focusFn()

    id.value = uniqid()
    useEventBus(`validate-${id.value}`).on(() => {
        validateLogic(props, modelState, input, internalError, emit)
    })

    useEventBus(`clear-error-${id.value}`).on(() => {
        resetError()
    })
})
</script>

<style lang="scss" scoped>
.label-input-wrapper.disabled > div {
    transform: none;
}

.textarea-wrapper {
    position: relative;
    padding-bottom: 10px;
    --button-offset: -6px;
    --secret-right-padding: 28px;

    --layer-textarea: 1;
    --layer-mask: 2;
    --layer-buttons: 3;

    .secret-button {
        position: absolute;
        top: var(--button-offset);
        right: var(--button-offset);
        z-index: var(--layer-buttons);
    }

    textarea {
        &.transparent {
            opacity: 0;
        }

        z-index: var(--layer-textarea);

        &.secret {
            padding-right: var(--secret-right-padding);
        }
    }

    .mask {
        height: 100%;
        min-height: 120px;
        font-family: var(--font-family-primary);
        overflow: auto;
        font-size: 16px;
        line-height: normal;
        font-weight: 500;
        padding: 2px;
        padding-right: var(--secret-right-padding);
        position: absolute;
        top: 18px;
        right: 0;
        bottom: 0;
        left: 0;
        white-space: pre-wrap;
        word-break: break-word;
        user-select: none;
        z-index: var(--layer-mask);
    }
}

%blur {
    color: transparent;
    text-shadow: 0 0 8px rgba(0,0,0,0.5);
}
</style>
